import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import {
  categories,
  filterMovies,
  getCategoryBySlug,
  PAGE_SIZE,
} from "@/lib/movieData";
import FilterPanel from "./components/FilterPanel";

export default function CategoryPage() {
  const { type } = useParams();
  const category = getCategoryBySlug(type);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState(false);

  const region = searchParams.get("region") || "全部地区";
  const year = searchParams.get("year") || "全部年份";
  const sort = searchParams.get("sort") || "latest";
  const currentPage = Math.max(1, Number(searchParams.get("page") || "1") || 1);

  const list = useMemo(
    () => (category ? filterMovies({ type: category.type, region, year, sort }) : []),
    [category, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = list.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  usePageMeta({
    title: category ? `${category.name} - 秋霞电影` : "电影分类 - 秋霞电影",
    description: category
      ? `${category.name}分类页面，${category.description} 共收录 ${list.length} 部影片资料，支持按地区、年份筛选与热门度排序。`
      : "秋霞电影电影分类页面，按类型浏览影片资料与相关资讯。",
    keywords: category
      ? `${category.name},${category.type}电影,电影推荐,秋霞电影,电影资讯`
      : "电影分类,秋霞电影,电影推荐",
  });

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value && value !== "全部地区" && value !== "全部年份") next.set(key, value);
      else next.delete(key);
    });
    if (!("page" in patch)) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (sort !== "latest") next.set("sort", sort);
    setSearchParams(next, { replace: true });
    setSheetOpen(false);
  };

  if (!category) {
    return (
      <PageLayout>
        <div className="w-full px-4 md:px-6 py-24">
          <div className="max-w-[720px] mx-auto text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-error-warning-line text-2xl"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">没有找到这个分类</h1>
            <p className="mt-3 text-sm text-foreground-600">该分类可能已调整，试试从下方重新进入片库。</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {categories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="h-9 px-4 inline-flex items-center rounded-full border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  const hasFilter = region !== "全部地区" || year !== "全部年份";

  return (
    <PageLayout>
      <section className="w-full bg-primary-500 text-background-50">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-9 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-primary-200" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-300 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category/drama" className="hover:text-accent-300 transition-colors">
              电影分类
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{category.type}</span>
          </nav>

          <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-5">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl">{category.name}</h1>
              <p className="mt-3 max-w-[620px] text-sm leading-relaxed text-primary-100/80">
                {category.description}
              </p>
            </div>
            <div className="flex items-center gap-6 shrink-0">
              <div>
                <p className="font-heading text-2xl text-accent-300">{list.length}</p>
                <p className="mt-1 text-[11px] text-primary-200">当前筛选结果</p>
              </div>
              <span className="w-px h-9 bg-primary-300/40"></span>
              <div>
                <p className="font-heading text-2xl text-background-50">{category.en}</p>
                <p className="mt-1 text-[11px] text-primary-200">类型检索</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 hidden md:block">
            <FilterPanel
              region={region}
              year={year}
              sort={sort}
              onChange={(patch) => updateParams(patch)}
            />
          </div>

          <div className="md:hidden flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              筛选与排序
              {hasFilter && <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>}
            </button>
            <span className="text-xs text-foreground-500">共 {list.length} 部</span>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共 <span className="text-primary-600 font-medium">{list.length}</span> 部影片 · 第{" "}
              {safePage} / {totalPages} 页
            </p>
            <div className="hidden sm:flex items-center gap-2 text-xs text-foreground-500">
              <span>当前：</span>
              <span className="text-foreground-800">
                {region} / {year}
              </span>
            </div>
          </div>

          {pageItems.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-8">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="mt-8 rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
              <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-film-line text-2xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-xl text-foreground-950">没有匹配的影片</h2>
              <p className="mt-2 text-sm text-foreground-600">
                试试更换地区或年份，或者重置筛选条件重新浏览全部{category.name}。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}

          {totalPages > 1 && (
            <Pagination
              page={safePage}
              totalPages={totalPages}
              onChange={(next) => {
                updateParams({ page: String(next) });
                window.scrollTo({ top: 300, behavior: "smooth" });
              }}
            />
          )}

          <div className="mt-14 pt-8 border-t border-background-300/70">
            <p className="text-xs tracking-widest text-foreground-400 mb-4">其他分类</p>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((item) => item.slug !== category.slug)
                .map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className="h-9 px-4 inline-flex items-center rounded-full border border-background-300 bg-background-50 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {item.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      {sheetOpen && (
        <div className="fixed inset-0 z-[60] md:hidden flex items-end">
          <div
            className="absolute inset-0 bg-primary-950/50 backdrop-blur-sm"
            onClick={() => setSheetOpen(false)}
          ></div>
          <div className="relative w-full max-h-[82vh] overflow-y-auto bg-background-50 rounded-t-2xl p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setSheetOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <FilterPanel
              region={region}
              year={year}
              sort={sort}
              onChange={(patch) => updateParams(patch)}
            />
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 h-11 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 h-11 rounded-md bg-accent-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                查看 {list.length} 部影片
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}